import {BaseElement, BASIS, isEltInitableAsyncPending, MsgLabel, OSkinableInit} from "back/commons/basis";
import {Button} from "back/commons/widgets/buttons";
import {POPUP} from "back/commons/widgets/popups";
import {IReg, REG} from "lib/commons/registry";
import {DOM, JSX} from "lib/commons/xml/dom";
import {DOMSH} from "lib/commons/xml/domsh";
import {IDepotUiEnv} from "lib/store/depot";
import {IResFormCtx, IResFormIdentCtx, IResType, JCidInteractionMetas, JCidMetas, OResFormIdentConfig, resPath} from "lib/store/res";
import {OResTypesTreeInit, ResTypesTree} from "back/store/widgets/resTypesTree";
import {URLTREE} from "lib/store/urlTree";
import {ICloseable} from "back/commons/widgets/popupable";
import {FORMS} from "lib/commons/forms";
import {IArea} from "lib/commons/areas";

/**
 * Dialogue d'édition du processing et des metas identificatoires
 * Ce dialogue n'exécute pas le push cid mais retourne les paramètres Cid.
 * Ce dialogue fournit en retour un JCidMetas avec a minima les propriétés suivantes :
 * - path (path versionné si VCB)
 * - processing
 * - olderResId (= "" en mode concurence lâche, "000000A000" si connu, undefined si aucun prédécesseur pour la reprise des metas)
 * - et toutes le metas annexes constitutives de l'identité
 */
export interface EditPrcIdent extends BaseElement {
	initialize(init: OEditPrcIdentInit): this
}

export interface OEditPrcIdentInit extends OSkinableInit, OResFormIdentConfig {

	reg?: IReg<IDepotUiEnv>


	/** Metas prédéfinies (configuration de l'identForm via CID) */
	otherMetasIn?: JCidInteractionMetas


	/** Configuration de l'affichage des ResTypes. */
	resTypesTree?: OResTypesTreeInit

	/** Processing à sélectionner par défaut dans la liste définie par resTypesTree. */
	defaultPrc?: string

	/**
	 * Label du bouton, "Importer" par défaut.
	 * Si fonction dynamique, réévalué après chaque changement de targetPath ou de processing.
	 */
	doBtnLabel?: string | ((widget: EditPrcIdent) => string)

	/** Contenu prédéfini pour preview */
	preview?: File | Blob

	/** Contexte d'appel auprès duquel retourner le résultat avec toutes les metas indentificatoires. */
	holder?: ICloseable<JCidMetas | null>
}

export class EditPrcIdent extends BaseElement {

	/** Contexte */
	reg: IReg<IDepotUiEnv>;

	config: OEditPrcIdentInit;

	/** Nom dynamique, du brtn de validation. */
	doBtnLabel?: (widget: EditPrcIdent) => string

	/** Eléments UI */
	mainTag: HTMLElement;
	msgArea: MsgLabel;
	doBtn: Button;
	cancelBtn: Button;

	resTypesTree: ResTypesTree;
	identForm: HTMLFormElement;

	/** Etats internes */
	resType: IResType;
	currentIdentFormArea: IArea<IResFormCtx>;
	targetPath?: resPath;

	get holder(): ICloseable<Pick<JCidMetas, "path" | "processing" | "olderResId"> | null> {return this._holder || POPUP.findPopupableParent(this)}

	_holder?: ICloseable<Pick<JCidMetas, "path" | "processing" | "olderResId"> | null>

	protected _initialize(init: OEditPrcIdentInit) {
		this.reg = this.findReg(init);
		this._holder = init.holder;
		this.config = init;

		const sr = this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this.reg.installSkin("standard-dialog", sr);
		this.reg.installSkin("form-control-areas", sr); //Pour les inputs de l'identForm
		this._initAndInstallSkin(this.localName, init);

		if (init.preview && init.preview.type?.startsWith("image/")) {
			const img = sr.appendChild(<img id="preview"/>) as HTMLImageElement;
			img.src = URL.createObjectURL(init.preview);
			this.classList.add("preview");
		}
		this.mainTag = sr.appendChild(<main/>);
		this.resTypesTree = new ResTypesTree().initialize(BASIS.newInit(init.resTypesTree, this.reg));
		this.mainTag.appendChild(<div id="resTypes"><label>Traitement</label>{this.resTypesTree}</div>);
		this.addEventListener('grid-select', this.onResTypeSelected);
		this.identForm = this.mainTag.appendChild(<form/>) as HTMLFormElement;
		let doLabel: string;
		if (typeof init.doBtnLabel === "function") {
			this.doBtnLabel = init.doBtnLabel;
			doLabel = "";
		} else {
			doLabel = init.doBtnLabel || "Importer";
		}
		const footer = sr.appendChild(<div id="footer"/>);
		this.msgArea = footer.appendChild(<c-msg id="msg"/>) as MsgLabel;
		this.doBtn = footer.appendChild(<Button id="import" label={doLabel} class="default" ui-context="dialog" onclick={this.onDoBtn}/>) as Button;
		this.cancelBtn = footer.appendChild(<Button id="cancel" label="Annuler" ui-context="dialog" onclick={this.onCancelBtn}/>) as Button;
		this.onkeypress = this.onKeypress;

		//Select du meilleur resType.
		if (this.resTypesTree.countResTypesUntil(2) === 1) {
			//Un seul ResType dans la liste.
			//On sélectionne le 1er et on masque la zone de sélection du resType.
			this.resTypesTree.selectFirst();
			DOM.setHidden(this.resTypesTree.parentElement, true);
		} else {
			this.classList.add("resTypes");
			let prcFound = null;
			if (init.defaultPrc) {
				prcFound = this.resTypesTree.findAndSelect((resType) => resType.prc === init.defaultPrc);
			}
			if (!prcFound && init.leafName) {
				//sniff le resType
				prcFound = this.resTypesTree.findAndSelect((resType) => resType.matchResName(init.leafName));
			}
			if (!prcFound) {
				//Res voir Folder par défaut
				const defaultRes = this.reg.env.resTypes.defaultResFileType;
				if (defaultRes) prcFound = this.resTypesTree.findAndSelect((resType) => resType === defaultRes);
				if (!prcFound) {
					const defaultFolder = this.reg.env.resTypes.defaultFolderType;
					if (defaultFolder) prcFound = this.resTypesTree.findAndSelect((resType) => resType === defaultFolder);
				}
			}
		}
	}

	protected _refresh() {
		//appelé une fois inséré dans l'arbre.
		// Promise.resolve().then(() => {
		// 	//En asynch car dialog.showModal() écrase le focus.
		// 	if (!this.resType) {
		// 		//restype non trouvé, on focus le choix du restype
		// 		this.resTypesTree.grid.focus();
		// 	} else if (this.nameInput && !this.nameInput.readOnly) {
		// 		this.nameInput.focus();
		// 	} else {
		// 		const toFocus = DOMSH.findFlatNext(this.shadowRoot, this.shadowRoot, DOM.IS_focusable);
		// 		if (toFocus) toFocus.focus();
		// 	}
		// });
	}

	async makeCidParams(): Promise<JCidMetas> {
		if (this.resType) {
			if (await FORMS.checkAsyncValidity(this.identForm)) {
				const metas: JCidMetas = {
					processing: this.resType.prc
				}
				FORMS.formToJson(this.identForm, metas);
				return metas;
			} else {
				FORMS.reportValidity(this.identForm);
			}
		}
		return null;
	}

	async onResTypeSelected(ev?: CustomEvent) {
		if (ev) ev.stopImmediatePropagation();
		const newResType = this.resTypesTree.getSelectedResType();
		if (this.resType === newResType) return;
		this.resType = newResType;
		if (this.resType == null) {
			this.currentIdentFormArea = null;
			this.identForm.textContent = null;
			if (this.resTypesTree.countResTypesUntil(1) === 0) {
				this.msgArea.setCustomMsg("Aucun traitement n'est compatible", 'error');
			} else {
				this.msgArea.setCustomMsg("Sélectionnez un traitement dans la liste", 'warning');
			}
			this.doBtn.disabled = true;
			if (this.doBtnLabel) this.doBtnLabel(this);
			return;
		}
		const identFormArea = this.resType.resForm("editIdent");
		if (this.currentIdentFormArea === identFormArea) return;
		this.currentIdentFormArea = identFormArea;
		const currentTarget = this.targetPath;
		const ctx: IResFormIdentCtx = {
			reg: this.reg.env.universe.newDepotResUiRegInCreation(this.reg, null, this.resType),
			folderPath: currentTarget != null ? URLTREE.extractParentPath(currentTarget) : this.config.folderPath,
			folderUi: this.config.folderUi,
			leafName: currentTarget != null ? URLTREE.extractUnversionedLeafName(currentTarget) : this.config.leafName,
			leafUi: this.config.leafUi,
			version: currentTarget != null ? URLTREE.extractVersionLeaf(currentTarget) : this.config.version,
			versionUi: this.config.versionUi,
			resUpdatable: this.config.resUpdatable,
			buildControlLabel: true,
			callback: this
		};
		const form = await identFormArea.loadBody(ctx);
		this.identForm.textContent = null;
		this.identForm.appendChild(form);
		if(isEltInitableAsyncPending(form)) await form.initializedAsync; //XXX ajouter await en récursif sur tous les nodes shadows et descendants ?
		if (this.config.otherMetasIn) FORMS.jsonToForm(this.config.otherMetasIn, this.identForm);
		FORMS.findFirstFocusable(form)?.focus();
		this.msgArea.setCustomMsg(null);
		this.doBtn.disabled = false;
		if (this.doBtnLabel) this.doBtnLabel(this);
		const popup = POPUP.findPopupableParent(this);
		if (popup) popup.updatePosition();
	}

	async onKeypress(ev: KeyboardEvent) {
		if (ev.key === "Enter") {
			const r = await this.makeCidParams();
			if (r) this.holder.close(r);
		}
	}

	onCancelBtn(this: Button) {
		const me = DOMSH.findHost<EditPrcIdent>(this);
		me.holder.close();
	}

	async onDoBtn(this: Button) {
		if (this.disabled) return;
		const me = DOMSH.findHost<EditPrcIdent>(this);
		const r = await me.makeCidParams();
		if (r) me.holder.close(r);
	}

	/** cf IResFormIdentCtx.callback */
	identState(resState: 'invalid' | 'pending' | 'exist' | 'free' | 'inTrash', targetPath?: resPath | null, msg?: string, msgLevel?: 'info' | 'error' | 'warning' | 'valid') {
		if (this.identForm) {
			this.msgArea.setCustomMsg(msg, msgLevel);
			this.doBtn.disabled = resState === "invalid";
			if (this.doBtnLabel) this.doBtnLabel(this);
			if (resState === 'pending') FORMS.checkAsyncValidity(this.identForm);
		}
	}
}

REG.reg.registerSkin('store-edit-prcident', 1, /* language=CSS */ `
	#preview {
		flex: 1 3 auto;
		align-self: center;
		max-width: 60vw;
		max-height: 10em;
		/*min-height: 2em;*/
		/*height: 5em;*/
		margin: .5em;
	}

	main {
		flex: 1000;
		display: flex;
		min-width: 0;
		min-height: 0;
		flex-direction: column;
	}

	#resTypes {
		flex: 1;
		display: flex;
		flex-direction: column;
		min-width: 0;
		min-height: 5em;
		padding: .3em;
	}

	#preview + #resTypes {
		border-top: 1px solid var(--border-color);
	}

	store-restypes-tree {
		border: 1px solid var(--border-color);
	}

	#resTypes[hidden] {
		display: none;
	}

	label {
		color: var(--alt1-color);
	}

  :focus-visible {
		outline: var(--focus-outline);
	}

	form {
		margin: .5em;
	}

	#msg {
		flex: 1;
		margin: 0 .5em;
		justify-content: start;
	}
`);

customElements.define("store-edit-prcident", EditPrcIdent);